import { createContext, useContext, useEffect, useState } from 'react';
import { account, teams } from '../lib/appwrite';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [warehouseId, setWarehouseId] = useState(null);
    const [warehouseName, setWarehouseName] = useState(null);
    const [teamRoles, setTeamRoles] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkUserStatus();
        // eslint-disable-next-line
    }, []);

    const checkUserStatus = async () => {
        try {
            const accountDetails = await account.get();
            setUser(accountDetails);

            // Fetch User's Teams (Warehouses)
            const teamList = await teams.list();
            setAllTeams(teamList.teams);

            if (teamList.teams.length > 0) {
                // Check for previously selected warehouse in localStorage
                const storedWarehouseId = localStorage.getItem('selectedWarehouseId');
                if (storedWarehouseId && teamList.teams.find(t => t.$id === storedWarehouseId)) {
                    // Restore previous selection
                    await selectWarehouseInternal(storedWarehouseId, accountDetails.$id);
                } else if (teamList.teams.length === 1) {
                    // If only one team, auto-select it
                    await selectWarehouseInternal(teamList.teams[0].$id, accountDetails.$id);
                }
                // If multiple and no stored selection, do nothing (wait for user selection)
            } else {
                setWarehouseId(null);
                setWarehouseName(null);
                setTeamRoles([]);
            }

        } catch (error) {
            setUser(null);
            setWarehouseId(null);
            setWarehouseName(null);
            setWarehouseName(null);
            setTeamRoles([]);
            setAllTeams([]);
        } finally {
            setLoading(false);
        }
    };

    const selectWarehouseInternal = async (teamId, userId) => {
        try {
            const team = await teams.get(teamId);
            setWarehouseId(team.$id);
            setWarehouseName(team.name);
            localStorage.setItem('selectedWarehouseId', team.$id); // Persist selection

            // Fetch Roles for this Team
            try {
                setTeamRoles([]);
                const memberships = await teams.listMemberships(teamId);
                const myMembership = memberships.memberships.find(m => m.userId === userId);
                if (myMembership) {
                    setTeamRoles(myMembership.roles);
                }
            } catch (roleError) {
                console.error("Error fetching team roles:", roleError);
                setTeamRoles([]);
            }
        } catch (error) {
            console.error("Error selecting warehouse:", error);
        }
    };

    const selectWarehouse = async (teamId) => {
        await selectWarehouseInternal(teamId, user?.$id);
    };

    const login = async (email, password) => {
        try {
            // Ensure no stale session exists
            try {
                await account.deleteSession('current');
            } catch (ignore) {
                // Ignore error if no session exists or network fail
            }

            await account.createEmailPasswordSession(email, password);
            await checkUserStatus();
            return { success: true };
        } catch (error) {
            console.error("Login Error:", error);
            // Return the specific error to help debug
            return { success: false, error: error };
        }
    };

    const logout = async () => {
        try {
            await account.deleteSession('current');
            setUser(null);
            setWarehouseId(null);
            setWarehouseName(null);
            setTeamRoles([]);
            setAllTeams([]);
            localStorage.removeItem('selectedWarehouseId'); // Clear on logout
        } catch (error) {
            console.error(error);
        }
    };

    const resetWarehouseSelection = () => {
        setWarehouseId(null);
        setWarehouseName(null);
        setTeamRoles([]);
        localStorage.removeItem('selectedWarehouseId'); // Clear persisted selection
    };

    const contextData = {
        user,
        warehouseId,
        warehouseName,
        teamRoles,
        allTeams,
        selectWarehouse,
        resetWarehouseSelection,
        login,
        logout,
        loading
    };

    return (
        <AuthContext.Provider value={contextData}>
            {loading ? <p>Loading...</p> : children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => { return useContext(AuthContext) };
